<template>
  <div v-if="loading" class="tw-flex tw-justify-center tw-py-8">
    <WdsSpinner :size="24" indeterminate />
  </div>
  <WdsStack v-else-if="companyRecruiter" direction-column gap-custom="2rem">
    <!-- Type & Contact Information-->
    <WdsStack v-if="companyRecruiter.externalIdentifier" direction-row>
      <div class="tw-basis-1/2"><WdsText :text="$t('External identifier')" secondary /></div>
      <div class="tw-basis-1/2">
        <WdsText :text="companyRecruiter.externalIdentifier" primary />
      </div>
    </WdsStack>

    <WdsStack direction-row class="tw-min-w-0" gap-md>
      <div class="tw-basis-1/2">
        <WdsStack direction-column gap-sm>
          <WdsText :text="$t('Ownership type')" secondary />
          <WdsText v-if="!!companyRecruiter.recruiterFee" :text="$t('Referral fee')" primary />
          <WdsText
            v-if="companyRecruiter.recruiterManagesWorkers"
            :text="$t('Manages workers')"
            primary
          />
          <WdsText v-else :text="$t('Client manages workers')" primary />
        </WdsStack>
      </div>
      <div class="tw-basis-1/2 tw-min-w-0">
        <WdsStack direction-column gap-sm class="tw-min-w-0">
          <WdsText :text="$t('Contact information')" secondary />
          <WdsStack
            v-if="companyRecruiter.recruiter?.owner?.name"
            align-center
            gap-sm
            class="tw-min-w-0"
          >
            <WdsIcon name="user" :size="16" class="tw-flex-shrink-0" />
            <WdsText primary class="tw-min-w-0">
              <div class="tw-truncate tw-min-w-0">
                {{ companyRecruiter.recruiter.owner.name }}
              </div>
            </WdsText>
          </WdsStack>
          <WdsStack v-if="companyRecruiter.email" align-center gap-sm class="tw-min-w-0">
            <WdsIcon name="email" :size="16" weight="thin" class="tw-flex-shrink-0" />
            <WdsButtonGhost
              class="tw-group tw-flex tw-items-center tw-w-full tw-max-w-fit"
              :class="{ 'copied-text': copiedText.email }"
              data-test="recruiter-copy-email"
              @click="copyText('email', companyRecruiter.email)"
            >
              <span
                v-if="!!companyRecruiter.email"
                class="tw-line-clamp-1 tw-text-left tw-truncate"
                :title="companyRecruiter.email"
              >
                {{ companyRecruiter.email }}
              </span>
              <div v-if="!copiedText.email" class="tw-hidden group-hover:tw-inline-block">
                <WdsIcon :name="WdsIconNameEnum.COPY" :size="16" />
              </div>
              <WdsText v-if="copiedText.email" tertiary class="tw-break-normal">
                {{ $t('Copied!') }}
              </WdsText>
            </WdsButtonGhost>
          </WdsStack>
        </WdsStack>
      </div>
    </WdsStack>

    <WdsStack direction-column gap-md>
      <WdsStack>
        <WdsStack direction-column class="tw-basis-1/2" gap-xs>
          <WdsText :text="$t('Operating Markets')" secondary />
        </WdsStack>

        <WdsStack v-if="isCollapsible" direction-column class="tw-basis-1/2" align-end gap-xs>
          <WdsButtonGhost
            class="tw-font-medium tw-flex-shrink-0 tw-whitespace-nowrap"
            @click="isExpanded = !isExpanded"
          >
            <WdsStack justify-center align-center gap-xs>
              <WdsText :text="isExpanded ? $t('Show less') : $t('Show more')" />
              <WdsIcon
                name="chevron-down"
                :size="16"
                class="tw-transition-transform tw-duration-200"
                :class="isExpanded ? 'tw-rotate-180' : ''"
              />
            </WdsStack>
          </WdsButtonGhost>
        </WdsStack>
      </WdsStack>
      <div
        ref="marketsContainer"
        class="markets-container tw-relative tw-flex tw-flex-col tw-gap-4 tw-overflow-hidden"
        :class="{ 'markets-container--collapsed': isCollapsible && !isExpanded }"
        :style="isCollapsible ? containerMaxHeight : {}"
      >
        <WdsStack v-for="{ market, regions } in recruiterMarkets" :key="market.code" direction-row>
          <WdsStack direction-column class="tw-basis-1/2" gap-xs>
            <WdsStack direction-row align-start gap-xs>
              <WdsIcon
                v-if="market.code"
                :size="16"
                :name="`flag-${market.code.toLowerCase()}` as WdsIconNameEnum"
              />
              <WdsText :text="market.name" primary />
            </WdsStack>
            <div>
              <WdsLabel v-if="market.code === recruiterMarket?.code" primary subtle sm>
                {{ $t('Primary market') }}
              </WdsLabel>
            </div>
          </WdsStack>
          <WdsStack direction-column class="tw-basis-1/2" gap-xs>
            <WdsText v-if="regions.length === 0">
              {{ allRegionsLabel(market.regionLabel) }}
            </WdsText>
            <template v-else>
              <WdsText v-for="region in regions" :key="region.name">
                {{ region.name }}
              </WdsText>
            </template>
          </WdsStack>
        </WdsStack>
      </div>
    </WdsStack>

    <div class="tw-col-span-3 tw-flex tw-items-end tw-gap-2 tw-mb-4 -tw-mt-4"></div>

    <WdsStack direction-column gap-sm>
      <WdsText :text="$t('Tags added by your company')" secondary />
      <div v-if="tags.length > 0">
        <WdsTag
          v-for="tag in tags"
          :key="tag.label"
          class="tw-m-0.5 tw-max-w-full tag-label"
          primary
        >
          {{ tag.label }}
        </WdsTag>
      </div>
      <div v-else>
        <WdsButtonGhost data-test="recruiter-add-skill" @click="emit('openEditModal')">
          <WdsStack gap-xs align-center>
            <WdsIcon name="add" :size="16" />
            {{ $t('Add skill') }}
          </WdsStack>
        </WdsButtonGhost>
      </div>
    </WdsStack>

    <WdsStack v-if="showOnboardingSection" direction-row gap-sm>
      <WdsStack direction-column gap-md class="tw-basis-full">
        <WdsText :sub-text="$t('Onboarding')" />

        <WdsStack direction-column gap-sm>
          <WdsStack align-center gap-sm>
            <WdsStack class="tw-basis-1/2 tw-items-center tw-flex">
              <span
                class="tw-mr-1 tw-text-lg"
                :class="[
                  customFieldsComplete && 'tw-text-green-500',
                  !customFieldsComplete && 'tw-text-orange-500',
                ]"
              >
                &bull;
              </span>
              <WdsText>{{ $t('Additional information') }}</WdsText>
            </WdsStack>
            <WdsStack class="tw-basis-1/2">
              <WdsLabel
                sm
                subtle
                :success="customFieldsComplete"
                :warning="!customFieldsComplete"
                class="tw-capitalize"
              >
                {{ customFieldsComplete ? $t('completed') : $t('pending') }}
              </WdsLabel>
            </WdsStack>
          </WdsStack>
        </WdsStack>
        <CustomFieldValuesDisplay
          v-if="recruiterFieldValues.length > 0"
          :custom-field-values="recruiterFieldValues"
        />
      </WdsStack>
    </WdsStack>

    <!-- Custom fields (internal, not visible to SA) -->
    <WdsStack direction-column gap-md>
      <WdsText secondary>
        {{ $t('Custom fields') }} ({{ $t('not visible to the staffing agency') }})
      </WdsText>
      <div v-if="showAddCustomFieldsButton">
        <WdsButtonGhost primary @click="emit('openEditModal')">
          <WdsStack align-center gap-sm>
            <WdsIcon :name="WdsIconNameEnum.PLUS" :size="18" />
            {{ $t('Fill out custom fields') }}
          </WdsStack>
        </WdsButtonGhost>
      </div>
      <CustomFieldValuesDisplay
        v-else-if="internalFieldValues.length > 0"
        :custom-field-values="internalFieldValues"
      />
    </WdsStack>

    <div class="tw-pt-4 tw-mt-auto tw-border-t tw-border-gray-100">
      <WdsText secondary>
        {{ $t('Contact was invited on :date', { date: formattedInvitedAt }) }}
      </WdsText>
    </div>
  </WdsStack>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import {
  WdsStack,
  WdsIcon,
  WdsText,
  WdsTag,
  WdsLabel,
  WdsButtonGhost,
  WdsSpinner,
  WdsIconNameEnum,
} from '@worksome/wds-components'
import { useQuery } from '@vue/apollo-composable'
import { useClipboard } from '@vueuse/core'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { useFragment } from '@/gql'
import { COMPANY_RECRUITER_DETAIL_FRAGMENT } from '@/components/RecruiterDetailPanel/graphql/fragments'
import { CUSTOM_FIELD_VALUES_VIEW_FRAGMENT } from '@/components/Fieldable/graphql/fragments'
import CustomFieldValuesDisplay from '@/components/Fieldable/CustomFieldValuesDisplay.vue'
import { CompanyRecruiterDetailQuery } from '@/components/RecruiterDetailPanel/graphql/queries'

const props = defineProps<{
  companyRecruiterId: string
}>()

const emit = defineEmits(['openEditModal'])

const variables = computed(() => ({
  companyRecruiterId: props.companyRecruiterId,
}))

const { loading, result } = useQuery(CompanyRecruiterDetailQuery, variables, {
  clientId: 'publicApi',
})

const isExpanded = ref(false)
const marketsContainer = ref<HTMLElement | null>(null)

const containerMaxHeight = computed(() => {
  if (!isExpanded.value) {
    return { maxHeight: '200px' }
  }
  const height = marketsContainer.value?.scrollHeight
  return height ? { maxHeight: `${height}px` } : {}
})

const companyRecruiter = computed(() =>
  useFragment(COMPANY_RECRUITER_DETAIL_FRAGMENT, result.value?.companyRecruiter),
)

const recruiterMarket = computed(() => companyRecruiter.value?.recruiter?.market)

const recruiterMarkets = computed(() => {
  const markets = companyRecruiter.value?.recruiter?.availableMarkets ?? []
  const primaryCode = recruiterMarket.value?.code
  return [...markets].sort((a, b) => {
    if (a.market.code === primaryCode) return -1
    if (b.market.code === primaryCode) return 1
    return 0
  })
})

const COLLAPSIBLE_THRESHOLD = 7

const isCollapsible = computed(
  () =>
    recruiterMarkets.value.reduce((count, { regions }) => count + 1 + regions.length, 0) >=
    COLLAPSIBLE_THRESHOLD,
)

const tags = computed(
  () =>
    companyRecruiter.value?.tags
      .filter(({ name }) => name !== undefined && name !== null)
      .map(({ name }) => ({ label: `${name}`, primary: true })) ?? [],
)

const recruiterFieldValues = computed(() =>
  (companyRecruiter.value?.customFieldValues ?? []).filter((fv) => {
    const unwrapped = useFragment(CUSTOM_FIELD_VALUES_VIEW_FRAGMENT, fv)
    return unwrapped.customField.recruiterInputAllowed
  }),
)

const internalFieldValues = computed(() =>
  (companyRecruiter.value?.customFieldValues ?? []).filter((fv) => {
    const unwrapped = useFragment(CUSTOM_FIELD_VALUES_VIEW_FRAGMENT, fv)
    return !unwrapped.customField.recruiterInputAllowed
  }),
)

const customFieldsComplete = computed(
  () => companyRecruiter.value?.requiredCustomFieldsComplete ?? true,
)

const showOnboardingSection = computed(
  () => recruiterFieldValues.value.length > 0 || !customFieldsComplete.value,
)

const copiedText = ref({
  email: false,
})

const { copy } = useClipboard()

const copyText = (type: 'email', text: string) => {
  copy(text)
  copiedText.value[type] = true
  setTimeout(() => {
    copiedText.value[type] = false
  }, 1500)
}

const showAddCustomFieldsButton = computed(() => internalFieldValues.value.length === 0)

const formattedInvitedAt = computed(() => {
  const date = companyRecruiter.value?.invitedAt
  if (!date) return ''
  return new Date(date).toLocaleDateString()
})

const allRegionsLabel = (label: string | null | undefined) => {
  switch (label) {
    case 'states':
      return $t('All states')
    case 'provinces':
      return $t('All provinces')
    case 'countries':
      return $t('All countries')
    default:
      return $t('All regions')
  }
}
</script>

<style scoped lang="scss">
.markets-container {
  transition: max-height 0.3s ease-in-out;
}

.tag-label :deep(div) {
  max-width: 100;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.markets-container::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3rem;
  background: linear-gradient(to top, white, transparent);
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
}

.markets-container--collapsed::after {
  opacity: 1;
}
</style>
